import { useMotionTemplate, useMotionValue, useScroll, useTransform } from 'motion/react';
import { RefObject, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import useUIContext from '../../../../../ui/use-ui-context';
import { isSmallerSelector } from '../../../../layout/mediaquery';

const scrollThreshold = [0, 70];

enum ScrollDirection {
	UP = 'UP',
	DOWN = 'DOWN'
}

function useGetScrollPosition() {
	const { scrollbar } = useUIContext();
	const customScrollbarElement = useRef(scrollbar?.current?.container?.firstChild) as
		| RefObject<HTMLElement | null>
		| undefined;
	const hasCustomScrollbarElement = !!customScrollbarElement?.current;
	const { scrollY } = useScroll({
		container: hasCustomScrollbarElement ? customScrollbarElement : undefined
	});
	const pixelsScrolled = useMotionValue(0);

	useEffect(() => {
		function handleScroll(latestScroll: number) {
			if (latestScroll < 0) {
				return;
			}

			const isScrollingDown = (scrollY.getPrevious() ?? 0) - latestScroll < 0;
			const currentScrollDirection = isScrollingDown ? ScrollDirection.DOWN : ScrollDirection.UP;

			if (currentScrollDirection === ScrollDirection.UP) {
				pixelsScrolled.set(Math.max(latestScroll, scrollThreshold[0]));
				return;
			}

			pixelsScrolled.set(Math.min(latestScroll, scrollThreshold[1]));
		}

		return scrollY.on('change', handleScroll);
	}, [scrollY, pixelsScrolled]);

	return { pixelsScrolled };
}

/**
 * The purpose of this animation is to solve a problem where we need to add horizontal padding
 * to the top bar for wider screens and we want to do it smoothly.
 */
export default function useSportsbookTopbarAnimation() {
	const isSmaller = useSelector(isSmallerSelector);
	const { pixelsScrolled } = useGetScrollPosition();

	const height = useTransform(pixelsScrolled, scrollThreshold, [40, 64]);

	const paddingX = useTransform(pixelsScrolled, scrollThreshold, [0, 12]);
	const paddingY = useMotionValue(0);
	const padding = useMotionTemplate`${paddingY}px ${paddingX}px`;

	// No animation is needed for smaller screens
	if (isSmaller) {
		return {};
	}

	return { height, padding };
}
